// Guided walkthroughs — every module, and the catalogue Settings → Walkthroughs lists.
//
// Every module is keyed to the surface it plays on (view + wizard step). Steps target
// `data-tour` anchors in the real UI; a step whose anchor isn't in the DOM is skipped at
// runtime, so conditional UI (a flag-gated button, an owner-only nav item) degrades
// gracefully. One way a module runs: as part of a walkthrough record — the full tour the
// welcome dialog or Settings' Restart All starts, or one section started from Settings →
// Walkthroughs. Every walkthrough plays on made-up data (Drew, 2026-10-01: "all mock
// data … they're not typing, they're just clicking and seeing explanations"): the wizard
// opens on a sample client already filled in, My Clients shows sample clients, Downline
// shows a sample downline, and it all goes away when the walkthrough ends.
//
// WHO READS THIS: an advisor, not an engineer (Drew, 2026-09-30: "a regular user is
// not going to understand what this row is and what an org is"). Every tip says what
// the thing on screen is, what you'd use it for, and what to click next, in the words
// an advisor already uses. No builder's words — no row, org, panel, node, flag, lever,
// module, sheet, cascade. tutorial.test.tsx scans for them.
//
// IP SAFETY: this copy is compiled into the client bundle. It must explain concepts
// only — never rates, multipliers, thresholds, bracket numbers, or anything from the
// server-side engine. tutorial.test.tsx enforces this with a banned-pattern scan over
// every string in this file. The only digits allowed are the form numbers "1040" and
// the "1" in "K-1" / "Schedule 1" — so a Schedule SE box is "the Social Security wages
// box", never its form name.

export interface TourStep {
  /** CSS selector for the anchor. Omit for a centered (floating) step. */
  target?: string;
  title: string;
  body: string;
  /**
   * The spotlighted thing can be clicked while this tip is up. Off by default
   * (Drew, 2026-09-30: "if you click and type, it goes away; if you click All
   * clients, it goes away"): a tip is for looking, and a click on the thing it
   * points at would navigate away and end the tour. On only where the tip says
   * "click this now" and the walkthrough is built to continue from that click —
   * the wizard's Next, New Client, Add Scenario, Manage.
   */
  interactive?: boolean;
}

export type TourView = 'dashboard' | 'org-scenarios' | 'client-view' | 'wizard' | 'results' | 'compare' | 'team-management';

export const TOUR_VIEWS: readonly TourView[] = ['dashboard', 'org-scenarios', 'client-view', 'wizard', 'results', 'compare', 'team-management'];

export interface TourModule {
  id: string;
  /** AppView this module plays on. */
  view: TourView;
  /** For wizard modules: the step index this module belongs to. */
  wizardStep?: number;
  /**
   * Selector that must exist before the module starts. Defaults to the first
   * step's target. Holds the strategy-card module until the advisor actually
   * adds a strategy, the folder tour until there is a folder, and the Manage
   * sheet tour until the sheet is open.
   */
  waitFor?: string;
  /**
   * Selector that, once it APPEARS while this module is running, ends the
   * module and hands off to the next one. This is how "click Add Scenario"
   * flows straight into the strategy-card tour instead of leaving a stale
   * popup behind once the card exists. Only armed when the selector is absent
   * at module start, so a module never self-skips on something already there.
   */
  advanceWhen?: string;
  steps: TourStep[];
}

/**
 * Closing tip for each wizard section. Points at the wizard's own Next button
 * so the walkthrough never dead-ends on a blank screen: the advisor always
 * knows what to click to continue.
 */
const CONTINUE_STEP: TourStep = {
  target: '[data-tour="wizard-next"]',
  interactive: true,
  title: 'Click Next',
  body: 'This page is already completed for the sample client. On a real client, enter what applies and leave the rest at zero. When you are ready, click <strong>Next</strong> to continue.',
};

/** Marker id recorded when the welcome dialog is answered (either button). */
export const WELCOME_MODULE = 'welcome';

export const TOUR_MODULES: TourModule[] = [
  // ── The walkthrough pointer on the dashboard ──────────────────────────────────
  {
    id: 'practice-start',
    view: 'dashboard',
    waitFor: '[data-tour="practice-banner"]',
    steps: [
      {
        target: '[data-tour="practice-banner"]',
        title: 'Walkthrough',
        body: 'This tour uses a sample client, already entered and calculated. Nothing you see here is saved. Exit walkthrough returns you to your own work at any time.',
      },
      {
        target: '[data-tour="new-client"]',
        interactive: true,
        title: 'Start with a client',
        body: 'Every client begins with their current tax position. Strategies are then added on top and compared against it. Click <strong>New Client</strong> to continue; the sample client opens already completed.',
      },
    ],
  },
  // ── The walkthrough pointer on All Clients ────────────────────────────────────
  // A support admin with no client book (has_client_book = false) has no My
  // Clients page; All Clients is home, and every client they build is for an
  // advisor they pick (Drew, 2026-10-13: "start them on all clients so they can
  // see the agent picker to pick who they are building for"). New Client opens
  // the picker, which ends this module; the next one takes over on the picker.
  {
    id: 'practice-start-org',
    view: 'org-scenarios',
    waitFor: '[data-tour="practice-banner"]',
    advanceWhen: '[data-tour="new-client-picker"]',
    steps: [
      {
        target: '[data-tour="practice-banner"]',
        title: 'Walkthrough',
        body: 'This tour uses a sample client, already entered and calculated. Nothing you see here is saved. Exit walkthrough returns you to your own work at any time.',
      },
      {
        target: '[data-tour="new-client-org"]',
        interactive: true,
        title: 'Start a client for an advisor',
        body: 'Every client begins with their current tax position, and here it is built on an advisor\'s behalf and lands in their book. Click <strong>New Client</strong> to continue.',
      },
    ],
  },
  {
    id: 'practice-pick-advisor',
    view: 'org-scenarios',
    waitFor: '[data-tour="new-client-picker"]',
    steps: [
      {
        target: '[data-tour="new-client-picker"]',
        title: 'Who the client belongs to',
        body: 'Choose the advisor under <strong>Create under</strong>; the client is saved in their book and shows under their name. Close this tip, pick anyone, then click Start Client. The sample client opens already completed.',
      },
    ],
  },
  // ── My Clients: the folder grid ───────────────────────────────────────────────
  {
    id: 'dashboard',
    view: 'dashboard',
    waitFor: '[data-tour="client-folder"]',
    steps: [
      {
        target: '[data-tour="nav-my-clients"]',
        title: 'My Clients',
        body: 'Your home page. Each folder is one client and holds their current tax position and every strategy modeled for them.',
      },
      {
        target: '[data-tour="clients-heading"]',
        title: 'Client count',
        body: 'The number beside the heading is how many folders are showing. It updates as you search.',
      },
      {
        target: '[data-tour="clients-search"]',
        title: 'Search',
        body: 'Type part of a name to narrow the folders. Clear the box to show every client again.',
      },
      {
        target: '[data-tour="clients-sort"]',
        title: 'Sort order',
        body: 'Newest first, oldest first, last edited or alphabetical. Your choice is remembered.',
      },
      {
        target: '[data-tour="new-client"]',
        title: 'New Client',
        body: 'Starts a new client. If you leave partway through, the work is kept under Drafts and can be resumed later.',
      },
      {
        target: '[data-tour="clients-edit-folders"]',
        title: 'Edit folders',
        body: 'Select several folders at once to merge them into one client or delete them together. Dragging one folder onto another also merges them.',
      },
      {
        target: '[data-tour="client-folder"]',
        title: 'A client folder',
        body: 'Opening a folder shows the client\'s current tax position, every strategy modeled for them, and the compare, export and present controls. A stacked folder holds more than one version of the client.',
      },
      {
        target: '[data-tour="nav-drafts"]',
        title: 'Drafts',
        body: 'A client left unfinished waits here. Resume continues from where you stopped.',
      },
      {
        target: '[data-tour="nav-all-clients"]',
        title: 'All Clients',
        body: 'Every client across your whole team in one list. Filter by advisor, review a colleague\'s work, or start a client on someone\'s behalf.',
      },
      {
        target: '[data-tour="nav-settings"]',
        title: 'Settings',
        body: 'Your name, email and password. Walkthroughs, under Settings, replays any of these tours.',
      },
      {
        title: 'Next, open a client',
        body: 'Close this tip and click any folder. The walkthrough continues inside it.',
      },
    ],
  },
  // ── All Clients: the team's folder grid (home for a support admin) ───────────
  {
    id: 'all-clients',
    view: 'org-scenarios',
    waitFor: '[data-tour="client-folder"]',
    steps: [
      {
        target: '[data-tour="nav-all-clients"]',
        title: 'All Clients',
        body: 'Your home page. Every client across the whole team in one list, each folder holding that client\'s current tax position and every strategy modeled for them.',
      },
      {
        target: '[data-tour="clients-heading"]',
        title: 'Client count',
        body: 'The number beside the heading is how many folders are showing. It updates as you search or filter.',
      },
      {
        target: '[data-tour="clients-search"]',
        title: 'Search',
        body: 'Type part of a name to narrow the folders. Clear the box to show every client again.',
      },
      {
        target: '[data-tour="clients-advisor-filter"]',
        title: 'Filter by advisor',
        body: 'Shows one advisor\'s clients at a time. All agents brings the whole team back.',
      },
      {
        target: '[data-tour="clients-sort"]',
        title: 'Sort order',
        body: 'Newest first, oldest first, last edited or alphabetical. Your choice is remembered.',
      },
      {
        target: '[data-tour="new-client-org"]',
        title: 'New Client',
        body: 'Starts a client for an advisor you choose. The client is saved in their book and shows under their name here.',
      },
      {
        target: '[data-tour="client-folder"]',
        title: 'A client folder',
        body: 'Opening a folder shows the client\'s current tax position, every strategy modeled for them, and the compare, export and present controls. A stacked folder holds more than one version of the client.',
      },
      {
        target: '[data-tour="nav-settings"]',
        title: 'Settings',
        body: 'Your name, email and password. Walkthroughs, under Settings, replays any of these tours.',
      },
      {
        title: 'Next, open a client',
        body: 'Close this tip and click any folder. The walkthrough continues inside it.',
      },
    ],
  },
  // ── Inside a client: the strategy list ────────────────────────────────────────
  {
    id: 'client-view',
    view: 'client-view',
    steps: [
      {
        target: '[data-tour="client-group"]',
        title: 'Inside a client',
        body: 'The first line is the client\'s current tax position, the baseline. Each line below it is a strategy, showing what changed and the resulting tax.',
      },
      {
        target: '[data-tour="strategy-row"]',
        title: 'One line per strategy',
        body: 'Each line shows the Roth conversion, alt investment and capital gains in that strategy, with the income and total tax that result. Clicking the name opens the full breakdown.',
      },
      {
        target: '[data-tour="strategy-select"]',
        title: 'Select strategies',
        body: 'Tick the strategies to compare, export or present. The baseline is always included.',
      },
      {
        target: '[data-tour="strategy-rename"]',
        title: 'Rename a strategy',
        body: 'The pencil gives a strategy a name the client will recognize, such as a conversion split across two years. The name appears everywhere, including exports.',
      },
      {
        target: '[data-tour="strategy-drag"]',
        title: 'Reorder',
        body: 'Drag the handle to move a strategy up or down. The comparison follows the same order, with the baseline always first.',
      },
      {
        target: '[data-tour="strategy-menu"]',
        title: 'Strategy menu',
        body: 'View the full results, edit the numbers, duplicate the strategy, build a new strategy on top of it, or delete it.',
      },
      {
        target: '[data-tour="client-compare"]',
        title: 'Compare, Export, Present',
        body: 'The same controls as on the comparison page, applied to the ticked strategies: Compare shows them side by side, Export produces the PDF or Excel file, and Present opens the presentation view.',
      },
      {
        target: '[data-tour="client-add-scenario"]',
        title: 'Add Strategy',
        body: 'Adds another strategy to this client without re-entering their numbers.',
      },
      {
        target: '[data-tour="client-duplicate"]',
        title: 'Duplicate',
        body: 'Copies the whole client as a new version, for modeling next year without changing this one. The copy sits in the same folder.',
      },
      {
        target: '[data-tour="client-more"]',
        title: 'More',
        body: 'Deletes the ticked strategies, or the entire client. Both ask for confirmation first.',
      },
      {
        title: 'That is a client',
        body: 'Select strategies, compare them, then export or present. This tour can be replayed from <strong>Settings → Walkthroughs</strong>.',
      },
    ],
  },
  // ── Wizard, page by page ──────────────────────────────────────────────────────
  {
    id: 'wizard-filing',
    view: 'wizard',
    wizardStep: 0,
    steps: [
      {
        target: '[data-tour="wizard-steps"]',
        title: 'The pages of a client',
        body: 'A new client is entered page by page, in this order. Any page can be opened directly. Nothing is saved until Save & Calculate on the last page. For this walkthrough every page is already completed for a sample couple.',
      },
      {
        target: '[data-tour="import-1040"]',
        title: 'Import a 1040',
        body: 'With last year\'s return as a PDF from tax software, most fields fill automatically. A scanned return cannot be read. Review the imported figures afterwards.',
      },
      {
        target: '[data-tour="filing-basics"]',
        title: 'Filing status and state',
        body: 'The filing status and state of residence drive the whole calculation. Married filing jointly adds spouse fields on the following pages.',
      },
      {
        target: '[data-tour="filing-age"]',
        title: 'Age',
        body: 'Switch on the ages that apply. Over the higher age the client gets the larger standard deduction, the additional senior deduction, and Medicare premiums are included. Those premiums rise with income, so a gain or a conversion can move them up a tier. The sample couple are both over it. The lower age unlocks some states\' retirement income breaks; the New York switches control that state\'s pension exclusion.',
      },
      CONTINUE_STEP,
    ],
  },
  {
    id: 'wizard-income',
    view: 'wizard',
    wizardStep: 1,
    steps: [
      {
        target: '[data-tour="income-fields"]',
        title: 'Wages',
        body: 'Wages for the client and, on a joint return, the spouse. Each field names the 1040 line it comes from.',
      },
      {
        target: '[data-tour="income-pensions"]',
        title: 'Pensions and military retirement',
        body: 'Government, private and military pensions are entered separately because some states tax them differently. Spouse fields apply only on a joint return.',
      },
      {
        target: '[data-tour="income-investment"]',
        title: 'Interest and dividends',
        body: 'Include tax-exempt interest. It is not taxed, but it affects how much Social Security is taxable and the Medicare premium. Qualified dividends are taxed at the capital gains rate.',
      },
      {
        target: '[data-tour="income-retirement"]',
        title: 'Retirement and Social Security',
        body: 'IRA distributions and the gross Social Security benefit. The taxable portion of Social Security is calculated for you. A Roth conversion entered here is one that has already taken place this year; a conversion to model belongs on the Strategies page.',
      },
      {
        target: '[data-tour="income-capgains"]',
        title: 'Capital gains and other income',
        body: 'Short-term gains are taxed as ordinary income; long-term gains at the lower rate. Other income covers any Schedule 1 item without its own field.',
      },
      CONTINUE_STEP,
    ],
  },
  {
    id: 'wizard-business',
    view: 'wizard',
    wizardStep: 2,
    steps: [
      {
        target: '[data-tour="business-taxpayer"]',
        title: 'The client\'s business',
        body: 'Gross receipts and total expenses from Schedule C. The profit is taxed and drives self-employment tax. If the client also has wages from a job, enter them in the Social Security wages field; left blank, the wages from the Income page are used.',
      },
      {
        target: '[data-tour="business-spouse"]',
        title: 'The spouse\'s business',
        body: 'The same fields for the spouse on a joint return. Each person has their own Social Security wage limit, so keep each person\'s figures in their own fields.',
      },
      {
        target: '[data-tour="business-qbi"]',
        title: 'Qualified business income deduction',
        body: 'Business owners receive a deduction on their profit. For higher earners it depends on the wages the business pays and the property it owns, and it is phased out for specified service businesses such as medicine, law and consulting. The sample\'s consulting business is one, so the deduction disappears on the results.',
      },
      CONTINUE_STEP,
    ],
  },
  {
    id: 'wizard-rental',
    view: 'wizard',
    wizardStep: 3,
    steps: [
      {
        target: '[data-tour="rental-property"]',
        title: 'Rental property',
        body: 'Rents received and expenses paid, from Schedule E. Mark the rental active if the client materially participates; it changes how a loss is treated.',
      },
      {
        target: '[data-tour="rental-k1"]',
        title: 'Partnership and S corporation income',
        body: 'The figures from a K-1: ordinary business income, guaranteed payments, rental real estate and other income. K-1 business income also counts toward the qualified business income deduction.',
      },
      CONTINUE_STEP,
    ],
  },
  {
    id: 'wizard-deductions',
    view: 'wizard',
    wizardStep: 4,
    steps: [
      {
        target: '[data-tour="deductions-salt"]',
        title: 'Medical and taxes paid',
        body: 'Medical expenses count only above a percentage of income. State income tax, property tax and personal property tax are combined and capped, and the cap shrinks for high earners. Enter the amounts; the limits are applied for you. The sample pays well over the cap.',
      },
      {
        target: '[data-tour="deductions-interest"]',
        title: 'Interest paid',
        body: 'Mortgage interest, points and investment interest. Together with the rest of this page, these decide whether itemizing beats the standard deduction. The larger of the two is used automatically.',
      },
      {
        target: '[data-tour="deductions-charity"]',
        title: 'Charitable giving',
        body: 'Cash gifts and gifts of property have separate limits, each a percentage of income. This matters twice: it is a deduction here, and an alt investment on the Strategies page draws on the same property-gift room. The more room left here, the larger the alt can be.',
      },
      {
        target: '[data-tour="deductions-adjustments"]',
        title: 'Adjustments to income',
        body: 'Amounts deducted before income is counted: self-employed health insurance and retirement contributions, HSA, traditional IRA and student loan interest. These apply whether or not the client itemizes.',
      },
      CONTINUE_STEP,
    ],
  },
  {
    id: 'wizard-credits',
    view: 'wizard',
    wizardStep: 5,
    steps: [
      {
        target: '[data-tour="credits-payments"]',
        title: 'Tax already paid',
        body: 'Federal withholding, estimated payments and state withholding. They do not change the tax; they determine whether the client owes or receives a refund, shown on the results page.',
      },
      CONTINUE_STEP,
    ],
  },
  // ── Strategies page ───────────────────────────────────────────────────────────
  {
    id: 'wizard-strategies',
    view: 'wizard',
    wizardStep: 6,
    steps: [
      {
        target: '[data-tour="baseline-card"]',
        title: 'The baseline',
        body: 'The client\'s tax position as entered, with nothing changed. Every strategy is measured against it.',
      },
      {
        target: '[data-tour="add-strategy"]',
        title: 'Add Strategy to Compare',
        body: 'Adds a strategy on top of the baseline. Add as many as needed, or none for now. One is already on the page for the sample: a large long-term gain, a Roth conversion and an alt investment. The walkthrough covers it next.',
      },
    ],
  },
  {
    id: 'strategy-card',
    view: 'wizard',
    wizardStep: 6,
    waitFor: '[data-tour="ltcg-section"]',
    steps: [
      {
        target: '[data-tour="strategy-card-header"]',
        title: 'The strategy card',
        body: 'Each card is one strategy, labeled by what it changes: Roth, Alt, LTCG or a combination. The X removes the card. Every figure below updates as you type.',
      },
      {
        target: '[data-tour="ltcg-section"]',
        title: 'Long-term capital gains',
        body: 'Models selling investments this year within this strategy only; the baseline is unchanged. Realizing gains raises income, which widens the room for the alt investment\'s deduction, so this is often what allows a larger alt.',
      },
      {
        target: '[data-tour="roth-section"]',
        title: 'Roth conversion',
        body: 'Moves money from a traditional IRA to a Roth. Tax is paid now and the balance then grows tax-free. The results show the projected Roth value and the future tax avoided.',
      },
      {
        target: '[data-tour="roth-fia"]',
        title: 'Use FIA Strategy',
        body: 'Grows the converted balance the way a fixed indexed annuity would, instead of at a flat rate. The comparison page then includes a year-by-year chart for it.',
      },
      {
        target: '[data-tour="roth-autosolve"]',
        title: 'Auto-solve to fit Alt',
        body: 'Enter the alt investment first, then tick this. The conversion amount is solved so that income is just high enough for the alt\'s full deduction to count.',
      },
      {
        target: '[data-tour="roth-growth-years"]',
        title: 'Growth Years',
        body: 'How many years the Roth grows before its value is reported. Whole years only.',
      },
      {
        target: '[data-tour="roth-fill-bracket"]',
        title: 'Fill to bracket',
        body: 'Choose a bracket and how much room to leave. Fill sets the conversion to use up that bracket without spilling into the next.',
      },
      {
        target: '[data-tour="alt-section"]',
        title: 'Alt investment',
        body: 'The amount placed in the alternative investment. It produces a charitable deduction this year and returns cash over the following years. This section shows the tax saved, the cash returned and the net benefit.',
      },
      {
        target: '[data-tour="alt-automax"]',
        title: 'Auto-max from Roth',
        body: 'Sets the alt to the largest amount whose deduction counts in full, and keeps it there as the Roth or the gains change. It is ticked for the sample, so the alt shown is the maximum this income allows.',
      },
      {
        target: '[data-tour="alt-deduction"]',
        title: 'Implied Deduction',
        body: 'The charitable deduction this investment produces. If the amount exceeds the deduction limit the card says so; either enable Auto-solve above or reduce the amount.',
      },
      {
        target: '[data-tour="alt-conservative"]',
        title: 'Conservative',
        body: 'Shows the investment returning only the capital invested, with no gain. The deduction is unchanged. Use it to present the cautious case.',
      },
      {
        target: '[data-tour="strategy-summary"]',
        title: 'Where the strategy lands',
        body: 'Income, taxable income and the saving against the baseline, updated as you change the strategy.',
      },
      {
        target: '[data-tour="save-compare"]',
        title: 'Save & Compare',
        body: 'Saves the client and opens the side-by-side comparison of the baseline and every strategy on this page.',
      },
      {
        target: '[data-tour="save-calculate"]',
        interactive: true,
        title: 'Save & Calculate',
        body: 'Saves the client and shows the full tax breakdown for the baseline. In the walkthrough nothing is saved; the figures are simply calculated. Click <strong>Save & Calculate</strong> now to continue to the results.',
      },
    ],
  },
  // ── Results: one scenario, line by line ───────────────────────────────────────
  {
    id: 'results',
    view: 'results',
    steps: [
      {
        target: '[data-tour="results-metrics"]',
        title: 'The headline figures',
        body: 'Total tax, the effective rate, and the total cost: tax plus any money placed in the alt investment. A notice above these means the alternative minimum tax or the net investment income tax applied.',
      },
      {
        target: '[data-tour="results-charts"]',
        title: 'Where the tax comes from',
        body: 'The left chart splits the tax into ordinary tax, capital gains tax, additional taxes and state tax. The right chart walks from income down to tax, showing what the deductions removed.',
      },
      {
        target: '[data-tour="results-federal"]',
        title: 'Federal, line by line',
        body: 'Income, the deduction used, taxable income, each tax, then payments already made. The last line is the amount owed or refunded.',
      },
      {
        target: '[data-tour="results-state"]',
        title: 'State',
        body: 'State tax under the rules for the client\'s state, with state withholding deducted to show the amount owed or refunded.',
      },
      {
        target: '[data-tour="results-combined"]',
        title: 'Federal and state combined',
        body: 'The total of both and the overall effective rate. Medicare premium surcharges and the Roth projection appear below when they apply.',
      },
      {
        target: '[data-tour="results-create-scenario"]',
        title: 'Create Strategy',
        body: 'Starts a new strategy from this exact set of figures.',
      },
      {
        target: '[data-tour="back-link"]',
        title: 'Back',
        body: 'Returns to the client folder or the client list.',
      },
      {
        title: 'Next: the comparison',
        body: 'That is the baseline on its own. Click <strong>Got it</strong> to open the comparison, where the baseline and the strategy appear side by side.',
      },
    ],
  },
  // ── Comparison ────────────────────────────────────────────────────────────────
  {
    id: 'comparison',
    view: 'compare',
    // Ready on either comparison page. Without this the module waited for its
    // first stop, the old page's bottom-line card, which v3 never renders, so
    // with the flag on nothing played at all (Drew, 2026-10-12). The export
    // toolbar is on both pages.
    waitFor: '[data-tour="comparison-export"]',
    steps: [
      {
        target: '[data-tour="comparison-net-benefit"]',
        title: 'The bottom line',
        body: 'Each strategy against the baseline. The difference is broken into the tax this year, what the alt investment returns beyond the capital invested, and the future tax the Roth avoids.',
      },
      {
        target: '[data-tour="comparison-strategies"]',
        title: 'Strategies applied',
        body: 'The Roth conversion, alt investment and capital gains in each strategy, and the income they produce. This is where a client sees what each column is made of.',
      },
      {
        target: '[data-tour="comparison-snapshot"]',
        title: 'Current year snapshot',
        body: 'Total tax for the baseline and each strategy, this year only. A Roth conversion usually looks worse here because the tax is paid up front; the payoff appears in the charts below.',
      },
      {
        target: '[data-tour="comparison-cost-benefit"]',
        title: 'Cost and benefit',
        body: 'For each strategy, what the client puts in this year against what comes back over the years shown: tax avoided, cash returned from the alt, and the Roth growth. A strategy with a Roth conversion has a button to show a Roth-only chart.',
      },
      {
        target: '[data-tour="comparison-journey"]',
        title: 'Year by year',
        body: 'One chart per strategy on a shared scale, showing where the client stands each year from the year the money goes in until it has all come back. The lowest point is the most they are ever out of pocket.',
      },
      {
        target: '[data-tour="comparison-detail"]',
        title: 'Detailed comparison',
        body: 'Every line of the return: income, deductions, each tax, payments and total cost, one column per strategy. The baseline is always the first column. Lines that are zero everywhere are hidden.',
      },
      {
        target: '[data-tour="comparison-doors"]',
        title: 'Pay the tax, or put it to work',
        body: 'The decision framed for the client: pay the tax, put that money to work instead, or let it run for the full period. The same figures as the rest of the page.',
      },
      {
        target: '[data-tour="comparison-alt-takeaways"]',
        title: 'Alt key takeaways',
        body: 'The cash the alt investment returns each year, from the fund\'s published schedule scaled to the client\'s amount, plus the total return as a multiple and as an annual rate.',
      },
      // The comparison_v3 page, version 4 (Drew, 2026-11-03): its own sections, in
      // page order, top to bottom. The old cards above are absent when the flag is
      // on, and these are absent when it is off.
      {
        target: '[data-tour="comparison-v3-answer"]',
        title: 'The answer',
        body: 'The strategy that leaves the client furthest ahead of doing nothing, and why, in one sentence: what it does, how its deduction offsets the tax, and what comes back. Beside it, what goes out this year, what comes back over the period, and the year the client is whole again.',
      },
      {
        target: '[data-tour="comparison-v3-years"]',
        title: 'Cost Benefit',
        body: 'Every strategy on one chart, as a running total against doing nothing, the dashed line. Today the alt check goes out, on tax day the year\'s tax comes due, then the money comes back. Click a strategy\'s box at the end of its line to see it on its own, with the gap to doing nothing shaded.',
      },
      {
        target: '[data-tour="comparison-v3-breakdown"]',
        title: 'The breakdown',
        body: 'The strategy picked on the chart, or the one that ends furthest ahead. On the left, this year: the moves, the tax they create, the alt check and what the deduction takes off. On the right, where it lands at the end of the period. The buttons at the top pick another strategy.',
      },
      {
        target: '[data-tour="comparison-v3-return"]',
        title: 'This year',
        body: 'The return under each strategy, with the change against doing nothing beside every number: income, deductions, taxable income, federal and state tax, the federal bracket and the room left in it, and Medicare premiums when a strategy moves them. Green is better for the client; red is worse.',
      },
      {
        target: '[data-tour="comparison-v3-plans"]',
        title: 'What each strategy does',
        body: 'Each move with its own tax, one column per strategy. This year: the tax the moves create, what the alt\'s deduction takes off, the alt check and the total out of pocket. Then what comes back by the end of the period. Every column adds up from top to bottom.',
      },
      {
        target: '[data-tour="comparison-v3-investment"]',
        title: 'The investment on its own',
        body: 'The alt by itself, with no tax dollars mixed in: capital at risk, cash returned, the multiple and the annual return, then the cash back each year, from the fund\'s published schedule scaled to each strategy\'s check.',
      },
      {
        target: '[data-tour="comparison-order"]',
        title: 'Column order',
        body: 'Drag the name tags to reorder the columns for a meeting. The baseline stays first. This changes only this page.',
      },
      {
        target: '[data-tour="comparison-add-strategy"]',
        title: 'Add Strategy',
        body: 'Adds a new strategy for this client without leaving the comparison. It appears as a new column once saved.',
      },
      {
        target: '[data-tour="comparison-present"]',
        title: 'Present',
        body: 'Opens a presentation view of the strategies for walking a client through the decision.',
      },
      {
        target: '[data-tour="comparison-export-pdf"]',
        title: 'Export PDF',
        body: 'A printable report of this page in this order, with the disclaimer on every page.',
      },
      {
        target: '[data-tour="comparison-export-excel"]',
        title: 'Export Excel',
        body: 'The same comparison as a workbook: the summary, the comparison, the client\'s figures and the alt schedule, each on its own tab.',
      },
      {
        title: 'That is the full cycle',
        body: 'Baseline, strategies, comparison, export. To do it for a real client, click New Client on My Clients. Any of these tours can be replayed from <strong>Settings → Walkthroughs</strong>.',
      },
    ],
  },
  // ── Downline Management ───────────────────────────────────────────────────────
  {
    id: 'downline',
    view: 'team-management',
    waitFor: '[data-tour="downline-title"]',
    advanceWhen: '[data-tour="downline-sheet"]',
    steps: [
      {
        target: '[data-tour="downline-title"]',
        title: 'Downline Management',
        body: 'The agencies, teams, agents and clients under you. Settings made here apply to everyone below.',
      },
      {
        target: '[data-tour="downline-all-clients"]',
        title: 'All clients',
        body: 'Opens a list of every client under you, filterable by advisor.',
      },
      {
        target: '[data-tour="downline-activity"]',
        title: 'Activity log',
        body: 'A record of who did what and when: invitations sent, settings changed, people moved or deactivated.',
      },
      {
        target: '[data-tour="downline-invites"]',
        title: 'Invites',
        body: 'Every invitation sent under you: the recipient, where they will land and who sent it. Resend an expired invitation or revoke one. The number shows how many are still open.',
      },
      {
        target: '[data-tour="downline-search"]',
        title: 'Search',
        body: 'Type the name of any agency, team, agent or client. Choosing a result narrows the table to that part of your downline.',
      },
      {
        target: '[data-tour="downline-level"]',
        title: 'Filter by level',
        body: 'Narrows the table to just the agencies, just the teams or just the agents, or lists every client. Each entry shows where it sits in your downline.',
      },
      {
        target: '[data-tour="downline-home"]',
        title: 'The top of your downline',
        body: 'This bar is you. Clicking its name clears any search or filter and shows everything again.',
      },
      {
        target: '[data-tour="downline-flags"]',
        title: 'Strategy access',
        body: 'Switches alt investments and annuity growth on or off for everyone under you. Off here is off for all of them. On here can still be switched off for a single agency or person further down.',
      },
      {
        target: '[data-tour="downline-add"]',
        title: 'Add',
        body: 'Invites a new person or creates a new agency or team under you. You enter their email and, for an agency or team, its name. They receive an email with a link to sign up.',
      },
      {
        target: '[data-tour="downline-expand"]',
        title: 'Expand all',
        body: 'Opens every agency and team so everyone is visible. Collapse all closes them again. Clicking a single agency or team opens just that one.',
      },
      {
        target: '[data-tour="downline-tree"]',
        title: 'Reading the table',
        body: 'Each line is an agency, a team or a person. Members and Clients count everyone within it. Strategies shows what is enabled: struck through means switched off at that level, and missing means switched off at a level above.',
      },
      {
        target: '[data-tour="downline-manage"]',
        interactive: true,
        title: 'Manage',
        body: 'Opens the settings for that agency, team or person. Click <strong>Manage</strong> now and the walkthrough continues inside.',
      },
    ],
  },
  {
    id: 'downline-manage',
    view: 'team-management',
    waitFor: '[data-tour="downline-sheet"]',
    steps: [
      {
        target: '[data-tour="downline-sheet"]',
        title: 'Settings for one agency, team or person',
        body: 'The same window serves an agency, a team, an agent or an admin. Strategy access is at the top, then the people and clients within, then the account controls.',
      },
      {
        target: '[data-tour="downline-sheet-flags"]',
        title: 'Strategy access here',
        body: 'Each switch states whether it was set here or inherited from a level above. Reset to inherited removes the setting here so the level above applies again.',
      },
      {
        target: '[data-tour="downline-open-clients"]',
        title: 'Open clients',
        body: 'Shows this person\'s clients as they see them, without changing anything.',
      },
      {
        target: '[data-tour="downline-sheet-add"]',
        title: 'Add',
        body: 'Adds a person, agency or team inside this one. The same as the Add button at the top of the page, placed here.',
      },
      {
        target: '[data-tour="downline-view-as"]',
        title: 'View as',
        body: 'Shows the application exactly as this person sees it, for support or to check their setup. Nothing done while viewing as them is saved.',
      },
      {
        target: '[data-tour="downline-reset-password"]',
        title: 'Reset password',
        body: 'Emails this person a link to set a new password.',
      },
      {
        target: '[data-tour="downline-account"]',
        title: 'Rename',
        body: 'Changes the name shown for this agency, team or person.',
      },
      {
        target: '[data-tour="downline-suspend"]',
        title: 'Suspend',
        body: 'Locks this person out until it is lifted. Nothing is deleted, and Reactivate restores their access.',
      },
      {
        target: '[data-tour="downline-role"]',
        title: 'Agent or Admin',
        body: 'An admin can manage the people under them; an agent works only their own clients. Changing the role never moves anyone\'s clients.',
      },
      {
        target: '[data-tour="downline-client-book"]',
        title: 'Own clients',
        body: 'Most people work their own client list. Someone who only supports others, such as an assistant, works from All Clients instead. This button switches between the two.',
      },
      {
        target: '[data-tour="downline-move"]',
        title: 'Move',
        body: 'Moves this person, or this agency or team with everything in it, to another place under you. Clients go with them.',
      },
      {
        target: '[data-tour="downline-deactivate"]',
        title: 'Deactivate',
        body: 'Removes them from every screen and ends their access immediately. Their clients are kept under their name and they can be reactivated later. Nothing is deleted.',
      },
      {
        title: 'That is your downline',
        body: 'Search or filter to find anyone, Manage to change their settings, Add to grow the team. Close this window with the X in its corner. This tour can be replayed from <strong>Settings → Walkthroughs</strong>.',
      },
    ],
  },
];

/**
 * The next uncompleted module for the current surface, or null.
 * Modules are declared in flow order, so the first match is the right one.
 * `only` restricts the search to one walkthrough's modules (Settings → Start).
 */
export function nextModuleFor(
  view: string,
  wizardStep: number,
  completed: ReadonlySet<string>,
  only?: ReadonlySet<string>,
): TourModule | null {
  return candidateModulesFor(view, wizardStep, completed, only)[0] ?? null;
}

/**
 * Every uncompleted module for the surface, in flow order. The sequencer starts the
 * first one whose anchor is on screen: the folder tour waits for a folder while the
 * practice pointer, declared before it, plays on an empty dashboard.
 */
export function candidateModulesFor(
  view: string,
  wizardStep: number,
  completed: ReadonlySet<string>,
  only?: ReadonlySet<string>,
): TourModule[] {
  return TOUR_MODULES.filter(
    m =>
      !completed.has(m.id) &&
      (!only || only.has(m.id)) &&
      m.view === view &&
      (m.wizardStep === undefined || m.wizardStep === wizardStep),
  );
}

// ─── Walkthrough catalogue (Settings → Walkthroughs) ────────────────────────

/** How the host page gets the person onto the walkthrough's first surface. */
export type WalkthroughStart =
  /** Enter the walkthrough's practice mode (the wizard opens on the sample client) and go to the dashboard; the pointer takes it from there. */
  | 'practice'
  /** Go to My Clients, with the sample clients showing. */
  | 'dashboard'
  /** Go to All Clients, with the sample clients showing (a support admin's home). */
  | 'all-clients'
  /** Open the side-by-side comparison for the sample client. */
  | 'compare'
  /** Go to Downline Management. */
  | 'downline';

export interface Walkthrough {
  id: string;
  title: string;
  description: string;
  /** Module ids, in the order they play. */
  modules: string[];
  start: WalkthroughStart;
  /**
   * Only offered to people who can reach the surface: Downline Management to
   * owners and admins; My Clients to people with a client book, All Clients
   * to a support admin without one (has_client_book = false).
   */
  requires?: 'downline' | 'client-book' | 'no-client-book';
  /**
   * The hand-off tip shown at the end of the walkthrough before this one in the
   * full tour (Drew, 2026-09-30: "when done be like, click on My Clients, here
   * is your client book … here is your downline"). It points at where the next
   * section lives; Got it takes the person there.
   */
  intro?: TourStep;
}

export const WALKTHROUGHS: Walkthrough[] = [
  {
    id: 'build-client',
    title: 'Building a client',
    description:
      'A sample client from start to finish: every page of the wizard completed, a strategy already on the Strategies page (a large gain, a Roth conversion and an alt investment), then the results and the side-by-side comparison. Nothing to enter, nothing saved.',
    modules: [
      'practice-start',
      'practice-start-org',
      'practice-pick-advisor',
      'wizard-filing',
      'wizard-income',
      'wizard-business',
      'wizard-rental',
      'wizard-deductions',
      'wizard-credits',
      'wizard-strategies',
      'strategy-card',
      'results',
      'comparison',
    ],
    start: 'practice',
  },
  {
    id: 'my-clients',
    title: 'My Clients',
    description:
      'The home page: finding and sorting clients, drafts and folder clean-up. Then inside a client: the list of strategies, renaming and reordering them, and the compare, export and present controls.',
    modules: ['dashboard', 'client-view'],
    start: 'dashboard',
    requires: 'client-book',
    intro: {
      target: '[data-tour="nav-my-clients"]',
      title: 'Next: My Clients',
      body: 'Every client you save is listed here, in the sidebar. Click <strong>Got it</strong> to go there and walk through your client list.',
    },
  },
  {
    id: 'all-clients',
    title: 'All Clients',
    description:
      'The home page for someone who builds clients on the advisors\' behalf: finding, filtering and sorting the team\'s clients and starting one for an advisor. Then inside a client: the list of strategies, renaming and reordering them, and the compare, export and present controls.',
    modules: ['all-clients', 'client-view'],
    start: 'all-clients',
    requires: 'no-client-book',
    intro: {
      target: '[data-tour="nav-all-clients"]',
      title: 'Next: All Clients',
      body: 'Every client across the team is listed here, in the sidebar. Click <strong>Got it</strong> to go there and walk through the list.',
    },
  },
  {
    id: 'comparison',
    title: 'The comparison',
    description:
      'The side-by-side page, one section at a time: the bottom line, what each strategy changes, the charts, every line of the return, and the controls to reorder, present and export. Opens on a sample client with a strategy already saved.',
    modules: ['comparison'],
    start: 'compare',
  },
  {
    id: 'downline',
    title: 'Downline Management',
    description:
      'The people under you: finding anyone, strategy access, adding people and agencies, invitations and the activity log. Then the settings for one person: role, moving and deactivating.',
    modules: ['downline', 'downline-manage'],
    start: 'downline',
    requires: 'downline',
    intro: {
      target: '[data-tour="nav-downline"]',
      title: 'Next: Downline Management',
      body: 'The people under you are managed here, in the sidebar. Click <strong>Got it</strong> to go there.',
    },
  },
];

/**
 * The full tour, in order: build a client, then My Clients, then Downline for
 * those who have one. One flow (Drew, 2026-09-30: "it's one walkthrough, they can
 * go through, and then sections they can replay later"). Each section hands off
 * to the next with its `intro` tip.
 */
export const FULL_TOUR_ORDER: readonly string[] = ['build-client', 'my-clients', 'all-clients', 'downline'];

/**
 * Whether a walkthrough is offered to this person. Downline Management to
 * owners and admins; My Clients to anyone with a client book; All Clients only
 * to a support admin without one, whose home it is.
 */
export function walkthroughVisible(
  requires: Walkthrough['requires'],
  who: { canSeeDownline: boolean; noClientBook: boolean },
): boolean {
  if (requires === 'downline') return who.canSeeDownline;
  if (requires === 'client-book') return !who.noClientBook;
  if (requires === 'no-client-book') return who.noClientBook;
  return true;
}

/** The full tour for one person: the order above, minus what they can't see. */
export function fullTourFor(visible: readonly Walkthrough[]): string[] {
  const ids = new Set(visible.map(w => w.id));
  return FULL_TOUR_ORDER.filter(id => ids.has(id));
}

/**
 * A module's steps, with the next section's hand-off tip in place of the
 * module's own closing tip when the tour continues. Only the last module of a
 * walkthrough hands off; a closing tip with no target is replaced, anything
 * else gets the hand-off appended.
 */
export function stepsWithHandoff(mod: TourModule, active: { id: string; chain?: string[] } | null): TourStep[] {
  if (!active?.chain?.length) return mod.steps;
  const wt = walkthroughById(active.id);
  if (!wt || wt.modules[wt.modules.length - 1] !== mod.id) return mod.steps;
  const next = walkthroughById(active.chain[0]);
  if (!next?.intro) return mod.steps;
  const last = mod.steps[mod.steps.length - 1];
  const base = last && !last.target ? mod.steps.slice(0, -1) : mod.steps;
  return [...base, next.intro];
}

export function walkthroughById(id: string): Walkthrough | undefined {
  return WALKTHROUGHS.find(w => w.id === id);
}
